import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, FileText, ClipboardCheck, PenTool, Download } from 'lucide-react';
import ComplianceQuiz from './ComplianceQuiz';
import DigitalSignature from './DigitalSignature';

const ComplianceFlow = () => {
  const [currentStep, setCurrentStep] = useState('manual'); // manual, quiz, signature, complete
  const [quizResults, setQuizResults] = useState(null);
  const [signatureData, setSignatureData] = useState(null);

  const steps = [
    { id: 'manual', label: 'Review Manual', icon: FileText, completed: currentStep !== 'manual' },
    { id: 'quiz', label: 'Complete Quiz', icon: ClipboardCheck, completed: quizResults !== null },
    { id: 'signature', label: 'Sign Acknowledgment', icon: PenTool, completed: signatureData !== null },
    { id: 'complete', label: 'Complete', icon: CheckCircle, completed: signatureData !== null }
  ];

  const handleManualReviewed = () => {
    setCurrentStep('quiz');
  };

  const handleQuizComplete = (results) => {
    if (results.passed) {
      setQuizResults(results);
      setCurrentStep('signature');
    }
  };

  const handleSignatureComplete = (data) => {
    setSignatureData(data);
    setCurrentStep('complete');
  };

  // Manual Review Step
  if (currentStep === 'manual') {
    return (
      <div className="space-y-6">
        {/* Progress Indicator */}
        <div className="flex items-center justify-between mb-8">
          {steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    step.completed
                      ? 'bg-green-500 text-white'
                      : currentStep === step.id
                      ? 'bg-[#BCA480] text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  <step.icon className="h-6 w-6" />
                </div>
                <span className="text-xs mt-2 text-gray-600">{step.label}</span>
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-1 mx-4 ${step.completed ? 'bg-green-500' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <Card>
          <CardHeader className="bg-gradient-to-r from-[#BCA480]/10 to-white">
            <CardTitle className="text-2xl">Step 1: Review Office Policies & Procedures Manual</CardTitle>
            <CardDescription className="text-base">
              Please review the complete policy manual before proceeding to the quiz
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-6">
              {/* Policy Manual Display */}
              <div className="border-2 border-[#BCA480] rounded-lg p-6 bg-[#BCA480]/5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <FileText className="h-8 w-8 text-[#BCA480]" />
                    <div>
                      <h3 className="font-bold text-lg">Bespoke Realty Office Policies and Procedures Manual</h3>
                      <p className="text-sm text-gray-600">Required reading for all agents</p>
                    </div>
                  </div>
                  <Button variant="outline" asChild>
                    <a 
                      href="https://customer-assets.emergentagent.com/job_agent-platform-28/artifacts/y8oqwm21_Bespoke%20Realty%20Office%20Policies%20and%20Proceedures_Rev1_25.pdf" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      download
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download PDF
                    </a>
                  </Button>
                </div>

                {/* PDF preview + fallback buttons */}
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <iframe
                    src="https://docs.google.com/gview?url=https%3A%2F%2Fcustomer-assets.emergentagent.com%2Fjob_agent-platform-28%2Fartifacts%2Fy8oqwm21_Bespoke%2520Realty%2520Office%2520Policies%2520and%2520Proceedures_Rev1_25.pdf&embedded=true"
                    className="w-full h-[600px]"
                    title="Policy Manual"
                    data-testid="policy-manual-iframe"
                  />
                  <div className="p-3 bg-gray-50 border-t text-center text-xs text-gray-600">
                    Preview not loading? {" "}
                    <a
                      href="https://customer-assets.emergentagent.com/job_agent-platform-28/artifacts/y8oqwm21_Bespoke%20Realty%20Office%20Policies%20and%20Proceedures_Rev1_25.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#BCA480] font-semibold underline"
                      data-testid="policy-manual-open-tab"
                    >
                      Open the PDF in a new tab →
                    </a>
                  </div>
                </div>
              </div>

              {/* Instructions */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-semibold mb-2">📋 Before You Continue:</h4>
                <ul className="space-y-1 ml-4 text-sm">
                  <li>• Review all sections of the policy manual thoroughly</li>
                  <li>• Take notes on key policies and procedures</li>
                  <li>• You will be tested on this material in the next step</li>
                  <li>• You must score 80% or higher on the quiz to proceed</li>
                </ul>
              </div>

              {/* Continue Button */}
              <div className="flex justify-end pt-4 border-t">
                <Button
                  onClick={handleManualReviewed}
                  className="bg-[#BCA480] hover:bg-[#a89470] px-8"
                  size="lg"
                >
                  I Have Reviewed the Manual - Continue to Quiz
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Quiz Step
  if (currentStep === 'quiz') {
    return (
      <div className="space-y-6">
        {/* Progress Indicator */}
        <div className="flex items-center justify-between mb-8">
          {steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    step.completed
                      ? 'bg-green-500 text-white'
                      : currentStep === step.id
                      ? 'bg-[#BCA480] text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  <step.icon className="h-6 w-6" />
                </div>
                <span className="text-xs mt-2 text-gray-600">{step.label}</span>
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-1 mx-4 ${step.completed ? 'bg-green-500' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <ComplianceQuiz onComplete={handleQuizComplete} />
      </div>
    );
  }

  // Signature Step
  if (currentStep === 'signature') {
    return (
      <div className="space-y-6">
        {/* Progress Indicator */}
        <div className="flex items-center justify-between mb-8">
          {steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    step.completed
                      ? 'bg-green-500 text-white'
                      : currentStep === step.id
                      ? 'bg-[#BCA480] text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  <step.icon className="h-6 w-6" />
                </div>
                <span className="text-xs mt-2 text-gray-600">{step.label}</span>
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-1 mx-4 ${step.completed ? 'bg-green-500' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <DigitalSignature quizResults={quizResults} onComplete={handleSignatureComplete} />
      </div>
    );
  }

  // Completion Step
  return (
    <div className="space-y-6">
      {/* Progress Indicator */}
      <div className="flex items-center justify-between mb-8">
        {steps.map((step, index) => (
          <React.Fragment key={step.id}>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-full flex items-center justify-center bg-green-500 text-white">
                <step.icon className="h-6 w-6" />
              </div>
              <span className="text-xs mt-2 text-gray-600">{step.label}</span>
            </div>
            {index < steps.length - 1 && (
              <div className="flex-1 h-1 mx-4 bg-green-500" />
            )}
          </React.Fragment>
        ))}
      </div>

      <Card>
        <CardHeader className="bg-green-50 border-b">
          <div className="flex items-center gap-4">
            <CheckCircle className="h-16 w-16 text-green-600" />
            <div>
              <CardTitle className="text-3xl">Compliance Complete!</CardTitle>
              <CardDescription className="text-lg mt-2">
                You have successfully completed all onboarding requirements
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-8">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <FileText className="h-8 w-8 text-blue-600 mb-2" />
                <p className="font-semibold">Manual Reviewed</p>
                <p className="text-sm text-gray-600">Completed ✓</p>
              </div>
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <ClipboardCheck className="h-8 w-8 text-green-600 mb-2" />
                <p className="font-semibold">Quiz Passed</p>
                <p className="text-sm text-gray-600">Score: {quizResults?.score}%</p>
              </div>
              <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <PenTool className="h-8 w-8 text-purple-600 mb-2" />
                <p className="font-semibold">Acknowledgment Signed</p>
                <p className="text-sm text-gray-600">Completed ✓</p>
              </div>
            </div>

            <div className="p-6 bg-gradient-to-r from-[#BCA480]/10 to-white border border-[#BCA480] rounded-lg">
              <h3 className="font-bold text-xl mb-3">🎉 Welcome to Bespoke Realty!</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Your compliance records have been logged in the system and admin dashboard. You are now fully onboarded and ready to start your journey with Bespoke Realty.
              </p>
              <p className="text-sm text-gray-600">
                A confirmation email has been sent to your registered email address with your completion details.
              </p>
            </div>

            <div className="flex gap-4 pt-4 border-t">
              <Button asChild className="bg-[#BCA480] hover:bg-[#a89470]">
                <a 
                  href="https://customer-assets.emergentagent.com/job_agent-platform-28/artifacts/y8oqwm21_Bespoke%20Realty%20Office%20Policies%20and%20Proceedures_Rev1_25.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  download
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Policy Manual for Your Records
                </a>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ComplianceFlow;
